import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { PaymentElement as StripePaymentElement } from "@stripe/react-stripe-js";
import type {
  DefaultValuesOption,
  StripeError,
  StripePaymentElementChangeEvent,
  StripePaymentElement as StripePaymentElementReadyEvent,
} from "@stripe/stripe-js";
import { useCallback, useMemo } from "react";

import { CheckoutError } from "../CheckoutError";
import { reportCheckoutError } from "../checkoutErrorHelpers";
import { usePaymentElementFields } from "../CheckoutPage/CheckoutForm/StripeElements/usePaymentElementFields";
import { usePaymentElementLayout } from "../CheckoutPage/CheckoutForm/StripeElements/usePaymentElementLayout";
import { usePaymentElementTerms } from "../CheckoutPage/CheckoutForm/StripeElements/usePaymentElementTerms";
import { useCheckoutSession } from "../contexts/CheckoutSessionContext";
import { useAddPaymentInfoTracking } from "../hooks/conversionTracking/useAddPaymentInfoTracking";
import { usePaywallCheckoutAttemptsContext } from "./PaywallCheckoutAttemptsContext";

// TODO: better handle "onLoadError" - we should only report errors to Bugsnag if the error blocks the submitting attempts.
export const PaywallsCheckoutAttemptsPaymentElement = () => {
  const {
    communityId,
    isExpressMode,
    memberAddress,
    memberEmail,
    memberId,
    memberName,
    paywallPriceId,
    sessionId,
    sessionStepId,
    setIsPaymentMethodLoaded,
    setIsMemberUsingDefaultPaymentMethod,
    setIsPaymentElementReady,
    setIsPaymentMethodComplete,
    setPaymentMethodType,
  } = usePaywallCheckoutAttemptsContext();

  // PAY-404 (Slice 1.2) -- `add_payment_info` fires on `event.complete` in
  // `onChange`, not on `onReady` (which fires on mount, before the member
  // has typed anything). Reuses the `funnelUuid` `begin_checkout` fired with
  // so both events share one anchor.
  const { funnelUuid, isRedirectReturnMount } = useCheckoutSession();
  // PAY-404 -- read live inside `onChange` so a price switch before
  // submission is reflected in whichever fire goes out. `amountDue` (not the
  // price's nominal amount) is what makes `value` genuinely `0` for a plain
  // trial with no upfront charge.
  const { getSelectedPrice, paywall, amountDue, previewMutation } =
    usePaywallCheckoutContext();
  const { trackPaymentInfoChange } = useAddPaymentInfoTracking({
    amountDue,
    checkoutSessionId: sessionId,
    currency: paywall.currency,
    funnelUuid,
    getSelectedPrice,
    isExpressMode,
    isPreviewReady: previewMutation.isSuccess && !previewMutation.isPending,
    isRedirectReturnMount,
    paywallId: paywall.id,
  });

  const defaultValues = useMemo(() => {
    const billingDetails: DefaultValuesOption["billingDetails"] = {};
    billingDetails.email = memberEmail;
    billingDetails.name = memberAddress.name || memberName;
    billingDetails.address = {
      city: memberAddress.city,
      country: memberAddress.country,
      line1: memberAddress.line1,
      line2: memberAddress.line2,
      postal_code: memberAddress.postalCode,
      state: memberAddress.state,
    };
    return { billingDetails };
  }, [memberAddress, memberEmail, memberName]);
  const fields = usePaymentElementFields();
  const layout = usePaymentElementLayout();
  const terms = usePaymentElementTerms();
  const options = useMemo(
    () => ({
      defaultValues,
      fields,
      terms,
      layout,
    }),
    [defaultValues, fields, layout, terms],
  );

  const onChange = useCallback(
    (event: StripePaymentElementChangeEvent) => {
      setIsPaymentMethodLoaded(true);
      setIsMemberUsingDefaultPaymentMethod(!!event.value?.payment_method?.id);
      setIsPaymentMethodComplete(event.complete);
      setPaymentMethodType(event.value.type);
      trackPaymentInfoChange(event);
    },
    [
      setIsPaymentMethodLoaded,
      setIsMemberUsingDefaultPaymentMethod,
      setIsPaymentMethodComplete,
      setPaymentMethodType,
      trackPaymentInfoChange,
    ],
  );
  const onLoadError = useCallback(
    (event: { elementType: string; error: StripeError }) => {
      const knownError = CheckoutError.fromStripeError(
        "PaywallsCheckoutAttemptsPaymentElement.onLoadError",
        event.error,
      );
      reportCheckoutError(knownError, {
        caller: knownError.caller,
        extra: knownError.getExtra(),
        communityId,
        communityMemberId: memberId,
        paywallPriceId,
        sessionId,
        sessionStepId,
      });
    },
    [communityId, memberId, paywallPriceId, sessionId, sessionStepId],
  );
  const onReady = useCallback(
    (event: StripePaymentElementReadyEvent) => {
      setIsPaymentElementReady(true);
      // In express mode, we don't focus the element since it's hidden
      if (memberId && !isExpressMode) {
        event.focus();
      }
    },
    [isExpressMode, memberId, setIsPaymentElementReady],
  );

  return (
    <StripePaymentElement
      options={options}
      onChange={onChange}
      onLoadError={onLoadError}
      onReady={onReady}
    />
  );
};
